import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LuCheck, LuChevronDown, LuCopy, LuIdCard, LuRefreshCw, LuSearch, LuShieldOff, LuTrash2, LuUpload, LuX } from 'react-icons/lu';
import { api, getApiData } from '../api';
import { confirmAction, promptReason, showError, showSuccess } from '../utils/adminAlerts';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = '.jpg,.jpeg,.png,.webp';
const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'webp'];

const statusStyles = {
  Pending: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
  Verified: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
  Rejected: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
  Revoked: 'bg-slate-200 text-slate-700 dark:bg-white/10 dark:text-gray-300',
};

const cardClass = 'rounded-2xl border border-slate-100 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-[#101010]';
const buttonClass =
  'inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-bold ${statusStyles[status] || statusStyles.Pending}`}>
      {status || 'No record'}
    </span>
  );
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

function validateFile(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXT.includes(ext)) return 'Only JPG, JPEG, PNG or WebP images are allowed.';
  if (file.size > MAX_BYTES) return 'Image must be 5 MB or smaller.';
  return '';
}

function CardUpload({ label, file, onFile, currentUrl, disabled }) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleChange = (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    const problem = validateFile(picked);
    setError(problem);
    onFile(problem ? null : picked);
  };

  const shown = previewUrl || currentUrl;

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-slate-700 dark:text-gray-200">{label}</p>
      <div className="flex aspect-[1.55/1] items-center justify-center overflow-hidden rounded-xl border border-dashed border-slate-300 bg-slate-50 dark:border-white/15 dark:bg-black/40">
        {shown ? (
          <img src={shown} alt={label} className="h-full w-full object-contain" />
        ) : (
          <span className="px-4 text-center text-xs text-slate-400">No image uploaded yet</span>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className={`${buttonClass} border border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5`}
        >
          <LuUpload size={16} /> {currentUrl || file ? 'Replace image' : 'Choose image'}
        </button>
        {file ? <span className="truncate text-xs text-slate-500 dark:text-gray-400">{file.name} (not saved yet)</span> : null}
        <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={handleChange} />
      </div>
      {error ? <p className="mt-2 text-xs font-medium text-red-600">{error}</p> : null}
    </div>
  );
}

const AdminEmployeeVerification = () => {
  const [records, setRecords] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [listOpen, setListOpen] = useState(false);

  const [employee, setEmployee] = useState(null);
  const [record, setRecord] = useState(null);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontUrl, setFrontUrl] = useState('');
  const [backUrl, setBackUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setListError('');
    try {
      setRecords(getApiData(await api.listEmployeeVerifications()) || []);
    } catch (err) {
      setListError(err.message);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Debounced ERP lookup: with no text it lists employees so one can be picked from the dropdown.
  useEffect(() => {
    if (!listOpen) return undefined;
    const term = query.trim();
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        setResults(getApiData(await api.searchEmployees(term)) || []);
        setSearchError('');
      } catch (err) {
        setResults([]);
        setSearchError(err.message);
      } finally {
        setSearching(false);
      }
    }, term ? 300 : 0);

    return () => clearTimeout(timer);
  }, [query, listOpen]);

  // Load stored card images (they require the bearer token) whenever the record changes.
  const frontKey = record?.hasFrontCard ? `${record.employeeId}-${record.updatedAt}` : '';
  const backKey = record?.hasBackCard ? `${record.employeeId}-${record.updatedAt}` : '';

  useEffect(() => {
    let revoked = false;
    let created = '';
    setFrontUrl('');
    if (frontKey) {
      api.fetchEmployeeCardBlobUrl(record.employeeId, 'front').then((url) => {
        if (revoked && url) URL.revokeObjectURL(url);
        else if (url) {
          created = url;
          setFrontUrl(url);
        }
      });
    }
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [frontKey]);

  useEffect(() => {
    let revoked = false;
    let created = '';
    setBackUrl('');
    if (backKey) {
      api.fetchEmployeeCardBlobUrl(record.employeeId, 'back').then((url) => {
        if (revoked && url) URL.revokeObjectURL(url);
        else if (url) {
          created = url;
          setBackUrl(url);
        }
      });
    }
    return () => {
      revoked = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [backKey]);

  const selectEmployee = async (emp) => {
    setEmployee(emp);
    setFrontFile(null);
    setBackFile(null);
    setRecord(null);
    setResults([]);
    setQuery('');
    setListOpen(false);
    try {
      setRecord(getApiData(await api.getEmployeeVerification(emp.employeeId)));
    } catch {
      setRecord(null); // 404 = no record yet
    }
  };

  const applyRecord = (next, message) => {
    setRecord(next);
    setEmployee(next.employee || employee);
    setFrontFile(null);
    setBackFile(null);
    loadList();
    if (message) showSuccess(message);
  };

  const run = async (action, message) => {
    setBusy(true);
    try {
      applyRecord(getApiData(await action()), message);
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () =>
    run(() => api.createEmployeeVerification(employee.employeeId), 'Verification record created.');

  const handleSaveImages = () =>
    run(() => api.uploadEmployeeCards(employee.employeeId, { front: frontFile, back: backFile }), 'ID card images saved.');

  const handleApprove = async () => {
    const ok = await confirmAction({
      title: 'Approve verification?',
      text: `Anyone scanning the QR code for ${employee.employeeId} will see this employee as VERIFIED.`,
      confirmButtonText: 'Approve',
    });
    if (ok) run(() => api.approveEmployeeVerification(employee.employeeId), 'Employee verified.');
  };

  const handleReject = async () => {
    const reason = await promptReason({ title: 'Reject verification', placeholder: 'Reason for rejection' });
    if (reason) run(() => api.rejectEmployeeVerification(employee.employeeId, reason), 'Verification rejected.');
  };

  const handleRevoke = async () => {
    const ok = await confirmAction({
      title: 'Revoke verification?',
      text: 'The public page will immediately stop showing this employee as verified.',
      confirmButtonText: 'Revoke',
      icon: 'warning',
    });
    if (ok) run(() => api.revokeEmployeeVerification(employee.employeeId), 'Verification revoked.');
  };

  const deleteRecord = async (employeeId) => {
    const ok = await confirmAction({
      title: 'Delete verification record?',
      text: `This permanently deletes the record and uploaded ID card images for ${employeeId}. The employee in the ERP is not affected, and the public page will show NOT VERIFIED.`,
      confirmButtonText: 'Yes, delete',
      icon: 'warning',
    });
    if (!ok) return;

    setBusy(true);
    try {
      await api.deleteEmployeeVerification(employeeId);
      if (record?.employeeId === employeeId) {
        setRecord(null);
        setFrontFile(null);
        setBackFile(null);
      }
      loadList();
      showSuccess('Verification record deleted.');
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(record.verificationUrl);
      showSuccess('Verification URL copied.');
    } catch {
      showError(record.verificationUrl, 'Copy this URL');
    }
  };

  const status = record?.status;
  const hasPending = Boolean(frontFile || backFile);
  const bothCards = record?.hasFrontCard && record?.hasBackCard;

  return (
    <div className="space-y-6">
      <div className={cardClass}>
        <div className="mb-5 flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
            <LuIdCard size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Employee Verification</h2>
            <p className="text-sm text-slate-500 dark:text-gray-400">
              Search an employee from the ERP, upload the finished ID card images, then approve the public QR verification.
            </p>
          </div>
        </div>

        <div className="relative">
          <LuSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => setListOpen(true)}
            placeholder="Select an employee from the ERP, or type an ID (e.g. RDS-0008) / name"
            className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-11 pr-10 text-sm text-slate-900 outline-none focus:border-primary dark:border-white/15 dark:bg-black dark:text-white"
          />
          <button
            type="button"
            onClick={() => setListOpen((open) => !open)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            aria-label="Toggle employee list"
          >
            <LuChevronDown size={18} className={listOpen ? 'rotate-180 transition' : 'transition'} />
          </button>
        </div>
        {listOpen ? (
          <div className="mt-3 max-h-80 overflow-y-auto rounded-xl border border-slate-100 dark:border-white/10">
            {searching ? <p className="px-4 py-3 text-sm text-slate-500">Loading employees from ERP…</p> : null}
            {searchError ? <p className="px-4 py-3 text-sm font-medium text-red-600">{searchError}</p> : null}
            {!searching && !searchError && results.length === 0 ? (
              <p className="px-4 py-3 text-sm text-slate-500">No employees found.</p>
            ) : null}
            <ul className="divide-y divide-slate-100 dark:divide-white/10">
              {results.map((item) => (
                <li key={item.employeeId}>
                  <button
                    type="button"
                    onClick={() => selectEmployee(item)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    <span>
                      <span className="font-semibold text-slate-900 dark:text-white">{item.fullName}</span>
                      <span className="ml-2 text-slate-500 dark:text-gray-400">{item.employeeId}</span>
                    </span>
                    {!item.isActive ? <span className="text-xs font-semibold text-red-600">Resigned</span> : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {employee ? (
        <div className={cardClass}>
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">{employee.fullName}</h3>
              <p className="text-sm text-slate-500 dark:text-gray-400">{employee.employeeId}</p>
            </div>
            <div className="flex items-center gap-3">
              {!employee.isActive ? (
                <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-800">Resigned in ERP</span>
              ) : null}
              <StatusBadge status={status} />
            </div>
          </div>

          <dl className="mb-6 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Employee ID', employee.employeeId],
              ['Designation (ERP code)', employee.position || '—'],
              ['Department (ERP code)', employee.department || '—'],
              ['Employment date', formatDate(employee.employmentDate)],
            ].map(([term, value]) => (
              <div key={term}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{term}</dt>
                <dd className="mt-1 font-semibold text-slate-900 dark:text-white">{value}</dd>
              </div>
            ))}
          </dl>

          {!record ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center dark:border-white/15">
              <p className="mb-4 text-sm text-slate-500 dark:text-gray-400">No verification record exists for this employee yet.</p>
              <button type="button" disabled={busy} onClick={handleCreate} className={`${buttonClass} bg-primary text-black hover:brightness-95`}>
                Create verification record
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-6 md:grid-cols-2">
                <CardUpload label="Front ID card" file={frontFile} onFile={setFrontFile} currentUrl={frontUrl} disabled={busy} />
                <CardUpload label="Back ID card" file={backFile} onFile={setBackFile} currentUrl={backUrl} disabled={busy} />
              </div>

              {status === 'Verified' && hasPending ? (
                <p className="mt-4 rounded-xl bg-amber-50 p-3 text-xs font-medium text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                  Replacing images on a verified record sets it back to Pending until you approve it again.
                </p>
              ) : null}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button type="button" disabled={busy || !hasPending} onClick={handleSaveImages} className={`${buttonClass} bg-primary text-black hover:brightness-95`}>
                  <LuUpload size={16} /> Save images
                </button>
                {status !== 'Verified' ? (
                  <button
                    type="button"
                    disabled={busy || hasPending || !bothCards}
                    onClick={handleApprove}
                    title={!bothCards ? 'Upload both card images first' : hasPending ? 'Save the new images first' : ''}
                    className={`${buttonClass} bg-green-600 text-white hover:bg-green-700`}
                  >
                    <LuCheck size={16} /> Approve
                  </button>
                ) : null}
                {status === 'Pending' || status === 'Rejected' ? (
                  <button type="button" disabled={busy || status === 'Rejected'} onClick={handleReject} className={`${buttonClass} border border-red-300 text-red-700 hover:bg-red-50`}>
                    <LuX size={16} /> Reject
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => deleteRecord(employee.employeeId)}
                  className={`${buttonClass} ml-auto border border-red-300 text-red-700 hover:bg-red-50`}
                >
                  <LuTrash2 size={16} /> Delete
                </button>
                {status === 'Verified' ? (
                  <button type="button" disabled={busy} onClick={handleRevoke} className={`${buttonClass} border border-red-300 text-red-700 hover:bg-red-50`}>
                    <LuShieldOff size={16} /> Revoke
                  </button>
                ) : null}
              </div>

              {status === 'Rejected' && record.rejectionReason ? (
                <p className="mt-4 text-sm text-red-700 dark:text-red-400">Rejection reason: {record.rejectionReason}</p>
              ) : null}
              {status === 'Verified' ? (
                <p className="mt-4 text-sm text-slate-500 dark:text-gray-400">
                  Verified by {record.verifiedByName || 'an administrator'} on {formatDate(record.verifiedAt)}.
                </p>
              ) : null}

              <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 p-4 text-sm dark:bg-white/5">
                <span className="font-semibold text-slate-700 dark:text-gray-200">QR target:</span>
                <a href={record.verificationUrl} target="_blank" rel="noreferrer" className="break-all text-primary underline">
                  {record.verificationUrl}
                </a>
                <button type="button" onClick={copyUrl} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 dark:text-gray-300">
                  <LuCopy size={14} /> Copy
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      <div className={cardClass}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">All verification records</h3>
          <button type="button" onClick={loadList} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900 dark:text-gray-300">
            <LuRefreshCw size={14} /> Refresh
          </button>
        </div>
        {loadingList ? <p className="text-sm text-slate-500">Loading…</p> : null}
        {listError ? <p className="text-sm font-medium text-red-600">{listError}</p> : null}
        {!loadingList && !listError && records.length === 0 ? (
          <p className="text-sm text-slate-500">No records yet. Search an employee above to start.</p>
        ) : null}
        {records.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-2 pr-4">Employee</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Cards</th>
                  <th className="py-2 pr-4">Verified</th>
                  <th className="py-2 text-right">Delete</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/10">
                {records.map((item) => (
                  <tr
                    key={item.employeeId}
                    onClick={() => item.employee && selectEmployee(item.employee)}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    <td className="py-3 pr-4">
                      <span className="font-semibold text-slate-900 dark:text-white">{item.employee?.fullName || item.employeeId}</span>
                      <span className="ml-2 text-slate-500 dark:text-gray-400">{item.employeeId}</span>
                    </td>
                    <td className="py-3 pr-4"><StatusBadge status={item.status} /></td>
                    <td className="py-3 pr-4 text-slate-600 dark:text-gray-300">
                      {item.hasFrontCard ? 'Front ✓' : 'Front ✗'} · {item.hasBackCard ? 'Back ✓' : 'Back ✗'}
                    </td>
                    <td className="py-3 pr-4 text-slate-600 dark:text-gray-300">{formatDate(item.verifiedAt)}</td>
                    <td className="py-3 text-right">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteRecord(item.employeeId);
                        }}
                        className="rounded-lg p-2 text-red-600 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-500/10"
                        aria-label={`Delete verification for ${item.employeeId}`}
                        title="Delete"
                      >
                        <LuTrash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AdminEmployeeVerification;
